/* eslint-disable */
import { ArrowLeftOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card, Col, Flex, Input, message, Modal, Row,
  Typography
} from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import Step1 from './Step1';
import Step2 from './Step2';
import Step3, { RequestStaffForm, Step3Ref } from './Step3';

import Loading from 'components/atoms/Loading';
import useDebounceFn from 'hooks/useDebounceFn';
import CodeNotFound from 'pages/CodeNotFound';
import {
  createStaffService, getProjectService, getStaffService, registerStaffHasInMasterDataService
} from 'services/project';
import { BankItem, RequestStaffInfo, StaffDataType, StaffHasInMasterData } from 'services/project/types';
import { StaffContractEnum } from 'services/staff/types';
import { contractTypeOptions, NoTaxCode } from 'utils/constants';
import mapModifiers, { detectErrorByCode, removeVietnameseDiacriticsAndUppercase } from 'utils/functions';
import { eventFormSchema, regisFormSchema, regisFormSchemaStep2 } from 'utils/schemas';

export interface TableForm {
  idCardNumber: string;
}

const defaultValues: RequestStaffForm = {
  fullname: '',
  taxCode: '',
  phoneNumber: '',
  idCardNumber: '',
  idCardIssuedAt: undefined,
  idCardIssuedBy: '',
  permanentAddress: '',
  contractType: {
    label: StaffContractEnum[1],
    value: StaffContractEnum['Part-time']
  },
  dob: undefined,
  socialInsuranceNumber: '',
  socialInsuranceJoinDate: undefined,
  socialInsuranceImage: undefined,
  socialInsuranceImagePath: '',
  idCardFrontImage: undefined,
  idCardFrontPath: '',
  idCardBackImage: undefined,
  idCardBackPath: '',
  bankAccounts: [],
  isAgree: true,
};

const Home: React.FC = () => {
  const [step, setStep] = React.useState(1);
  const [messageCheckId, setMessageCheckId] = React.useState('');
  const [messageCheckIdModal, setMessageCheckIdModal] = React.useState('');
  const slugParams = useParams<{ code: string }>();

  const step3Ref = useRef<Step3Ref>(null);

  // React-hook-from
  const method = useForm<TableForm>({
    defaultValues: {
      idCardNumber: '',
    },
    mode: 'onChange',
    resolver: yupResolver(eventFormSchema),
  });

  const regisMethod = useForm<RequestStaffForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(step > 2 ? regisFormSchema : regisFormSchemaStep2),
  });

  const idCardNumberValue = method.watch('idCardNumber');

  useDebounceFn(() => {
    setMessageCheckId('');
  }, 0, [idCardNumberValue]);

  // React-query
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: projectData, isLoading } = useQuery(
    ['getProjectService'],
    () => getProjectService(slugParams.code || ''),
  );

  const handleReuseExistedIdCard = (staffDataParam: StaffDataType) => {
    setMessageCheckIdModal('');
    setStep(2);
    method.reset({
      idCardNumber: staffDataParam.staffData.idCardNumber
    });
    regisMethod.reset({
      bankAccounts: staffDataParam?.bankData?.map((bk) => ({
        bankAccount: bk.accountNumber,
        bankBranch: {
          label: bk.branchName,
          value: bk.branchName
        },
        bankId: bk.bankId,
        bankName: {
          label: bk.bankName,
          value: bk.bankName
        },
        bankOwner: removeVietnameseDiacriticsAndUppercase(staffDataParam?.staffData.fullname),
        city: {
          label: bk.province?.name,
          value: bk.province?.code
        },
        isActive: bk.accountDefault,
        isDefault: bk.accountDefault,
        isDisabled: true // disable when update
      })) || undefined,
      contractType: contractTypeOptions.find((i) => i.value === staffDataParam?.staffData.contractType),
      dob: staffDataParam.staffData.dob,
      fullname: staffDataParam?.staffData.fullname,
      idCardFrontPath: staffDataParam?.staffData.frontIdCardPath,
      idCardFrontImage: staffDataParam?.staffData.frontIdCard ? {
        file: undefined,
        pathSubmit: staffDataParam?.staffData.frontIdCard || '',
      } : undefined,
      idCardBackPath: staffDataParam?.staffData.backIdCardPath,
      idCardBackImage: staffDataParam?.staffData.backIdCard ? {
        file: undefined,
        pathSubmit: staffDataParam?.staffData.backIdCard || '',
      } : undefined,
      idCardIssuedAt: staffDataParam?.staffData.idCardIssuedAt,
      idCardIssuedBy: staffDataParam?.staffData.idCardIssuedBy,
      idCardNumber: staffDataParam?.staffData.idCardNumber,
      isAgree: true,
      permanentAddress: staffDataParam?.staffData.permanentAddress,
      phoneNumber: staffDataParam?.staffData.phoneNumber,
      socialInsuranceImage: {
        file: undefined,
        pathSubmit: staffDataParam?.staffData.socialInsuranceImage,
      },
      socialInsuranceImagePath: staffDataParam?.staffData.socialInsuranceImage,
      socialInsuranceJoinDate: staffDataParam?.staffData.socialInsuranceJoinDate,
      socialInsuranceNumber: staffDataParam?.staffData.socialInsuranceNumber,
      taxCode: staffDataParam?.staffData.taxCode === '' ? NoTaxCode.value : staffDataParam?.staffData.taxCode,
    });
  };

  const { mutate: getStaffMutate, isLoading: loadingStaff, data: staffData } = useMutation(
    ['staffService'],
    (idCardNumber: string) => getStaffService(idCardNumber, slugParams.code || ''),
    {
      onSuccess: (data) => {
        if (data?.staffData) {
          if (step === 1 || step === 2) {
            handleReuseExistedIdCard(data);
          }
          if (step === 3) {
            setMessageCheckIdModal(`Số CCCD ${data?.staffData.idCardNumber} đã có trong hệ thống. Bạn có muốn đăng kí chương trình với số CCCD này ?`);
          }
        }
      },
      onError: (err: AxiosError) => {
        if (step === 3) {
          if (err?.code === 'hasInStaffAssign') {
            setMessageCheckIdModal('Số CCCD của bạn là nhân viên giao việc, không thể đăng ký chương trình!');
          } else if (err?.code === 'hasInTeam') {
            console.log('hasInTeam');
            setMessageCheckIdModal('Số CCCD của bạn đã đăng ký ở chương trình này!');
          } else if (err.response?.status === 404) {
            setMessageCheckIdModal('');
            step3Ref.current?.handleFillForm();
          }
        } else {
          setStep(1);
          if (err?.code === 'hasInStaffAssign') {
            setMessageCheckId('Số CCCD của bạn là nhân viên giao việc, không thể đăng ký chương trình!');
          } else if (err?.code === 'hasInTeam') {
            setMessageCheckId('Số CCCD của bạn đã đăng ký ở chương trình này!');
          } else if (err.response?.status === 404) {
            setStep(3);
            regisMethod.reset({
              ...defaultValues,
              idCardNumber: method.getValues('idCardNumber')
            });
          } else {
            message.error('Có lỗi xảy ra, vui lòng thử lại');
          }
        }
      }
    }
  );

  const { mutate: createStaffMutate, isLoading: createLoading } = useMutation(
    ['createStaffMuate'],
    (params: RequestStaffInfo) => createStaffService(params),
    {
      onSuccess: () => {
        setStep(1);
        regisMethod.reset(defaultValues);
        method.reset({
          idCardNumber: ''
        });
        message.success('Đăng ký thành công');
      },
      onError: (errors: ErrorResponse) => {
        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((item) => {
            regisMethod.setError(
              item.field,
              { message: detectErrorByCode(item.code, item.field, item.message), type: 'validate' },
              {
                shouldFocus: true,
              }
            );
            message.error(detectErrorByCode(item.code, item.field, item.message));
          });
          return;
        }
        if ((errors as ErrorAPIResponseTypes).code === 'staffExists') {
          message.error('Số CCCD của bạn đã tồn tại!');
        } else {
          message.error('Có lỗi xảy ra, vui lòng thử lại');
        }
      }
    }
  );

  const {
    mutate: registerStaffHasInMasterDataMutate,
    isLoading: isLoadingRegisterStaffHasInMasterData
  } = useMutation(
    ['registerStaffHasInMasterDataMutate'],
    (params: {
      idCardNumber: string,
      params: StaffHasInMasterData
    }) => registerStaffHasInMasterDataService(params.idCardNumber, params.params),
    {
      onSuccess: () => {
        setStep(1);
        regisMethod.reset(defaultValues);
        method.reset({
          idCardNumber: ''
        });
        message.success('Đăng ký thành công');
      },
      onError: (err: AxiosError) => {
        if (err.response?.status === 404) {
          message.error('Không tìm thấy thông tin');
        } else {
          message.error('Có lỗi xảy ra, vui lòng thử lại');
        }
      }
    }
  );

  const handleStep1 = async () => {
    const isValid = await method.trigger();

    if (!isValid) {
      return;
    }

    const data = method.getValues();
    getStaffMutate(data.idCardNumber);
  };

  const handleStep2 = async () => {
    const triggerRegister = await regisMethod.trigger();
    if (!triggerRegister) return;

    const idCardNumberParam = method.getValues('idCardNumber');
    if (slugParams.code && idCardNumberParam) {
      const bankAccount: BankItem[] = regisMethod.getValues('bankAccounts').map((bank) => ({
        accountDefault: bank.isDefault,
        accountName: bank.bankOwner,
        accountNumber: bank.bankAccount,
        bankId: bank.bankId || 0,
        isActive: !!bank.isActive,
      }));

      registerStaffHasInMasterDataMutate({
        idCardNumber: idCardNumberParam,
        params: {
          code: slugParams.code || '',
          banks: bankAccount || [],
        }
      });
    }
  };

  const handleStep3 = async () => {
    const isValid = await regisMethod.trigger();

    if (!isValid) {
      return;
    }

    const data = regisMethod.getValues();

    const {
      fullname, dob, idCardNumber, idCardIssuedAt,
      idCardIssuedBy, taxCode, permanentAddress, contractType,
      socialInsuranceNumber, socialInsuranceJoinDate, socialInsuranceImage,
      bankAccounts, idCardBackImage, idCardBackPath, idCardFrontImage, idCardFrontPath,
      phoneNumber
    } = data;

    const submitData: RequestStaffInfo = {
      code: projectData?.code || '',
      fullname,
      dob: dayjs(dob).format('YYYY-MM-DD'),
      idCardNumber,
      idCardIssuedAt: dayjs(idCardIssuedAt).format('YYYY-MM-DD'),
      taxCode: taxCode === NoTaxCode.value ? '' : taxCode,
      permanentAddress,
      socialInsuranceNumber,
      socialInsuranceJoinDate: dayjs(socialInsuranceJoinDate).format('YYYY-MM-DD'),
      socialInsuranceImage: socialInsuranceImage?.pathSubmit || '',
      contractType: contractType?.value,
      idCardIssuedBy: idCardIssuedBy || '',
      banks: bankAccounts.map((bank) => ({
        accountDefault: bank.isDefault,
        accountName: bank.bankOwner,
        accountNumber: bank.bankAccount,
        bankId: bank.bankId || 0,
        isActive: !!bank.isActive,
      })),
      frontIdCard: idCardFrontImage?.file ? idCardFrontImage?.pathSubmit : idCardFrontPath || '',
      backIdCard: idCardBackImage?.file ? idCardBackImage?.pathSubmit : idCardBackPath || '',
      phoneNumber,
    };

    createStaffMutate(submitData);
  };

  const handleOkCheckIdModal = () => {
    if (staffData) {
      handleReuseExistedIdCard(staffData);
    } else {
      setMessageCheckIdModal('');
    }
  };

  const handleCancelCheckIdModal = () => {
    setMessageCheckIdModal('');
  };

  if (isLoading) {
    return <Loading variant="fullscreen" colorLoading="secondary" />;
  }

  if (!projectData) {
    return <CodeNotFound status="404" />;
  }

  return (
    <div className="p-home">
      <Flex style={{ width: '100%', position: 'relative' }} justify="center" className="p-home_header">
        {
          step !== 1 && (
            <Button
              icon={<ArrowLeftOutlined size={24} />}
              size="middle"
              style={{ position: 'absolute', left: 0, top: 0 }}
              onClick={() => {
                setStep(1);
                method.reset({
                  idCardNumber: ''
                });
              }}
            >
              Quay lại
            </Button>
          )
        }
        <Typography.Title
          level={4}
          style={{ textAlign: 'center' }}
          className={mapModifiers('p-home_header-title', step.toString())}
        >
          ĐĂNG KÝ THAM GIA CHƯƠNG TRÌNH
        </Typography.Title>
      </Flex>
      <Card
        className="p-home__card"
        title={(
          <Typography.Title
            style={{ marginBottom: 0 }}
            level={4}
            className="p-home__card-title"
          >
            Thông tin chương trình
          </Typography.Title>
        )}
      >
        <Row gutter={12}>
          {/* <Col xs={24} sm={5} className="u-mb-8">
            <Typography.Text>
              Mã chương trình
            </Typography.Text>
            <div className="p-home__card__input">
              <Input
                size="large"
                readOnly
                value={projectData?.projectData?.code || ''}
              />
            </div>
          </Col>
          <Col xs={24} sm={5} className="u-mb-8">
            <Typography.Text>
              Tên khách hàng
            </Typography.Text>
            <div className="p-home__card__input">
              <Input
                size="large"
                readOnly
                value={projectData?.projectData?.customerName || ''}
              />
            </div>
          </Col> */}
          <Col xs={24} sm={12} className="u-mb-8">
            <Typography.Text>
              Tên chương trình
            </Typography.Text>
            <div className="p-home__card__input">
              <Input
                size="large"
                readOnly
                value={projectData?.projectData?.name || ''}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} className="u-mb-8">
            <Typography.Text>
              Người quản lý
            </Typography.Text>
            <div className="p-home__card__input">
              <Input
                size="large"
                readOnly
                value={projectData?.payrollData?.fullname || ''}
              />
            </div>
          </Col>
        </Row>
      </Card>
      <Card
        className="p-home__card"
        title={(
          <Typography.Title
            style={{ marginBottom: 0 }}
            level={4}
            className="p-home__card-title"
          >
            {`${step < 3 ? 'Nhập số CCCD' : 'Thông tin chung'}`}
          </Typography.Title>
        )}
      >
        {
          loadingStaff && (
            <Loading variant="fullscreen" colorLoading="secondary" />
          )
        }
        {(() => {
          switch (step) {
            case 1:
              return (
                <Step1
                  isLoading={loadingStaff}
                  method={method}
                  handleClick={handleStep1}
                  messageCheckId={messageCheckId}
                />
              );
            case 2:
              return (
                <Step2
                  method={method}
                  methodRegister={regisMethod}
                  staffData={staffData}
                  isLoading={loadingStaff || isLoadingRegisterStaffHasInMasterData}
                  handleClick={handleStep2}
                  buttonRecheck={{
                    isShow: true,
                    onHandleCheck: async (idNumber) => {
                      const isValid = await method.trigger();

                      if (!isValid) {
                        return;
                      }
                      method.reset({
                        idCardNumber: idNumber,
                      });

                      getStaffMutate(idNumber);
                    }
                  }}
                />
              );
            case 3:
              return (
                <Step3
                  method={regisMethod}
                  // view={!!staffData}
                  data={staffData}
                  loading={createLoading || loadingStaff}
                  handleClick={handleStep3}
                  ref={step3Ref}
                  handleBeforeUseIdCardData={(idCardNumber) => getStaffMutate(idCardNumber)}
                />
              );
            default:
              return '';
          }
        })()}
      </Card>
      <Modal
        title={(
          <Typography.Title
            level={3}
            style={{ textAlign: 'center' }}
          >
            CCCD đã tồn tại
          </Typography.Title>
        )}
        open={!!messageCheckIdModal}
        onOk={handleOkCheckIdModal}
        cancelButtonProps={{
          style: { display: staffData ? 'block' : 'none' }
        }}
        onCancel={handleCancelCheckIdModal}
        width={600}
      >
        <Typography.Text>
          {messageCheckIdModal}
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default Home;
